import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input } from 'antd';

import type { IWalletCashOutBrand, WalletCashOutBrandGroup } from '@share/common-types';

import './style.scss';

const GROUP_ORDER: WalletCashOutBrandGroup[] = ['PrepaidCard', 'GiftCard', 'Transfer', 'Charity'];

const GROUP_LABELS: Record<WalletCashOutBrandGroup, string> = {
  PrepaidCard: 'wallet.cashout.group.prepaid_card',
  GiftCard: 'wallet.cashout.group.gift_card',
  Transfer: 'wallet.cashout.group.transfer',
  Charity: 'wallet.cashout.group.charity',
};

/** Below this many brands a search box is noise. */
const SEARCH_THRESHOLD = 8;

/** Brands shown per section before "view more". */
const COLLAPSED_COUNT = 6;

interface IProps {
  brands: IWalletCashOutBrand[];
  selectedId: string | null;
  onSelect: (productId: string) => void;
}

/**
 * The catalog the member picks from, in sections (prepaid cards, gift cards, transfers, charity)
 * with a search box once the list is long. Brands without a group fall under gift cards.
 */
export function CashOutBrandPicker(props: IProps) {
  const { brands, selectedId, onSelect } = props;
  const intl = useIntl();

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Partial<Record<WalletCashOutBrandGroup, boolean>>>({});

  const sections = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term ? brands.filter((brand) => brand.name.toLowerCase().includes(term)) : brands;

    return GROUP_ORDER.map((group) => ({
      group,
      brands: matching.filter((brand) => (brand.group ?? 'GiftCard') === group),
    })).filter((section) => section.brands.length > 0);
  }, [brands, search]);

  return (
    <div className="cash-out-brand-picker">
      {brands.length > SEARCH_THRESHOLD ? (
        <Input.Search
          allowClear
          className="cash-out-brand-picker__search"
          placeholder={intl.formatMessage({ id: 'wallet.cashout.brand.search' }, { count: brands.length })}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      ) : null}

      {sections.length === 0 ? (
        <p className="wallet-cash-out__hint">
          <FormattedMessage id="wallet.cashout.brand.no_match" />
        </p>
      ) : null}

      {sections.map(({ group, brands: groupBrands }) => {
        // The selected brand stays visible even when its section is collapsed.
        const isExpanded = !!expanded[group] || !!search.trim();
        const visible = isExpanded
          ? groupBrands
          : groupBrands.filter(
              (brand, index) => index < COLLAPSED_COUNT || brand.productId === selectedId,
            );
        const hidden = groupBrands.length - visible.length;

        return (
          <section key={group} className="cash-out-brand-picker__section">
            <h4 className="cash-out-brand-picker__title">
              <FormattedMessage id={GROUP_LABELS[group]} />
              <span className="cash-out-brand-picker__count">{groupBrands.length}</span>
            </h4>

            <ul className="wallet-cash-out__brands">
              {visible.map((brand) => (
                <li key={brand.productId}>
                  <button
                    type="button"
                    className={`wallet-cash-out__brand ${brand.productId === selectedId ? 'selected' : ''}`}
                    aria-pressed={brand.productId === selectedId}
                    onClick={() => onSelect(brand.productId)}
                  >
                    {brand.imageUrl ? <img src={brand.imageUrl} alt="" /> : null}
                    <span>{brand.name}</span>
                  </button>
                </li>
              ))}
            </ul>

            {hidden > 0 ? (
              <button
                type="button"
                className="cash-out-brand-picker__more"
                onClick={() => setExpanded((current) => ({ ...current, [group]: true }))}
              >
                <FormattedMessage id="wallet.cashout.brand.more" values={{ count: hidden }} />
              </button>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
